import React from 'react'
import { Link } from 'react-router-dom'
import { SEOHead } from '../components/ui/SEOHead'
import { TerminalWindow } from '../components/terminal/TerminalWindow'
import { AsciiDivider } from '../components/terminal/AsciiDivider'

const EFFECTIVE_DATE = 'May 2026'

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ marginBottom: '28px' }}>
    <div
      style={{
        color: 'var(--accent-cyan)',
        fontFamily: "'JetBrains Mono', 'Courier New', monospace",
        fontSize: '0.88rem',
        fontWeight: 700,
        marginBottom: '10px',
      }}
    >
      ## {title}
    </div>
    <div
      style={{
        color: 'var(--text-secondary)',
        fontSize: '0.875rem',
        lineHeight: '1.85',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
      }}
    >
      {children}
    </div>
  </section>
)

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
    {items.map((item, i) => (
      <li key={i} style={{ marginBottom: '6px' }}>
        {item}
      </li>
    ))}
  </ul>
)

export const PrivacyPolicyPage: React.FC = () => (
  <>
    <SEOHead
      title="Privacy Policy"
      description="CodeToByte Privacy Policy — how we collect, use, and protect your information."
      url="/privacy"
    />

    <div style={{ maxWidth: '780px', margin: '0 auto', padding: '32px 0' }}>
      <TerminalWindow title="privacy-policy.md">
        <div
          style={{
            fontFamily: "'JetBrains Mono', 'Courier New', monospace",
            fontSize: '0.85rem',
            lineHeight: '1.8',
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: '24px' }}>
            <div
              style={{
                fontSize: '1.15rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: '6px',
              }}
            >
              Privacy Policy
            </div>
            <div style={{ color: 'var(--text-muted, #555)', fontSize: '0.78rem' }}>
              Last updated: {EFFECTIVE_DATE}
            </div>
          </div>

          <AsciiDivider char="─" width={50} />

          {/* Intro */}
          <div
            style={{
              color: 'var(--text-secondary)',
              fontSize: '0.875rem',
              lineHeight: '1.85',
              marginBottom: '28px',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            CodeToByte (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) is committed to protecting your
            privacy. This Privacy Policy explains what information we collect when you visit{' '}
            <strong>codetobyte.com</strong>, how we use it, and the choices you have. By using our
            website, you agree to the practices described in this policy.
          </div>

          <Section title="Information We Collect">
            <p style={{ marginTop: 0 }}>
              We collect only the information necessary to provide and improve our services:
            </p>

            <div
              style={{
                color: 'var(--accent-green)',
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: '0.82rem',
                margin: '12px 0 4px',
              }}
            >
              a) Account Information (Google Sign-In)
            </div>
            <p>
              When you choose to sign in with Google, we receive from Google your{' '}
              <strong>name</strong>, <strong>email address</strong>, and a unique{' '}
              <strong>Google account identifier</strong>. We do not receive or store your
              Google password. Sign-in is optional — you can read all articles without an
              account.
            </p>

            <div
              style={{
                color: 'var(--accent-green)',
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: '0.82rem',
                margin: '12px 0 4px',
              }}
            >
              b) Newsletter Subscription
            </div>
            <p>
              If you subscribe to our newsletter, we collect your <strong>email address</strong>{' '}
              and, optionally, your <strong>name</strong>. We store your subscription status
              (pending, active, or unsubscribed) and a secure unsubscribe token. You may
              unsubscribe at any time using the link in any email we send.
            </p>

            <div
              style={{
                color: 'var(--accent-green)',
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: '0.82rem',
                margin: '12px 0 4px',
              }}
            >
              c) Comments
            </div>
            <p>
              When you post a comment, we store your <strong>display name</strong>,{' '}
              <strong>email address</strong> (not shown publicly), <strong>comment content</strong>,
              and the timestamp. Your email is used solely for account identification and is
              never displayed on the site or shared with other users.
            </p>

            <div
              style={{
                color: 'var(--accent-green)',
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: '0.82rem',
                margin: '12px 0 4px',
              }}
            >
              d) Page View Analytics (Anonymous)
            </div>
            <p>
              To understand which content is most useful, we record anonymous page views. For
              each view we store an approximate <strong>geographic location</strong> (country,
              region, city), your browser&rsquo;s <strong>language setting</strong>, and your{' '}
              <strong>timezone</strong>. <strong>We do not store your IP address.</strong>{' '}
              Geographic data is resolved client-side by a third-party service (ipapi.co) before
              reaching our servers; only the resolved location fields are sent to us.
            </p>

            <div
              style={{
                color: 'var(--accent-green)',
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: '0.82rem',
                margin: '12px 0 4px',
              }}
            >
              e) Authentication Token
            </div>
            <p>
              When you sign in, a secure session token (JWT) is stored in your browser&rsquo;s{' '}
              <strong>localStorage</strong>. This token keeps you signed in across page
              navigations and expires automatically. We do not use tracking cookies.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="How We Use Your Information">
            <BulletList
              items={[
                'To authenticate you when you sign in with Google.',
                'To send you the newsletter and new article notifications you subscribed to.',
                'To display your comments on article pages.',
                'To understand aggregate readership patterns and improve content (using anonymous analytics).',
                'To respond to support or feedback requests you send us.',
                'To enforce our Terms and Conditions and maintain the safety of our community.',
              ]}
            />
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="What We Do Not Do">
            <BulletList
              items={[
                'We do not sell your personal information to anyone.',
                'We do not share your data with third parties for advertising or marketing purposes.',
                'We do not store your IP address.',
                'We do not use third-party advertising networks or tracking pixels.',
                'We do not use tracking cookies. Our only browser storage is the authentication token in localStorage.',
                'We do not send unsolicited emails. You will only receive emails you explicitly opted into.',
              ]}
            />
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Third-Party Services">
            <p style={{ marginTop: 0 }}>
              We use a small number of trusted third-party services to operate the site:
            </p>
            <BulletList
              items={[
                'Google LLC (Google Sign-In) — authenticates your account. Subject to Google\'s Privacy Policy.',
                'ipapi.co — resolves an approximate geographic location from your IP address, client-side. Only the resolved country/region/city is sent to our servers. No IP address is stored by us.',
                'Microsoft Azure Communication Services — sends transactional emails (subscription confirmation, welcome, unsubscribe confirmation, new article notifications).',
              ]}
            />
            <p>
              We do not use Google Analytics, Facebook Pixel, or any other behavioural
              advertising or tracking services.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Data Storage and Security">
            <p style={{ marginTop: 0 }}>
              Your data is stored on servers hosted on <strong>Microsoft Azure</strong>{' '}
              (cloud infrastructure). We implement reasonable technical and organisational measures
              to protect your information against unauthorised access, alteration, or disclosure.
              Authentication between the browser and our API uses HTTPS. Passwords are never
              stored (sign-in is handled entirely by Google OAuth).
            </p>
            <p>
              While we take security seriously, no system is completely immune to risk. We
              encourage you to use strong passwords on your Google account and to sign out of
              shared devices.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Data Retention">
            <p style={{ marginTop: 0 }}>
              We retain your personal data only for as long as necessary to fulfil the purposes
              described in this policy, comply with our legal obligations, and resolve disputes:
            </p>
            <BulletList
              items={[
                'Account data (name, email, Google ID): retained while your account is active. Deleted upon verified request.',
                'Newsletter subscriber data: retained until you unsubscribe or request deletion.',
                'Comments: retained indefinitely unless you request removal or a comment is removed for policy violations.',
                'Anonymous page view analytics: retained indefinitely in aggregate form.',
              ]}
            />
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Your Rights">
            <p style={{ marginTop: 0 }}>
              You have the right to:
            </p>
            <BulletList
              items={[
                'Access the personal data we hold about you.',
                'Request correction of inaccurate data.',
                'Request deletion of your account and associated personal data.',
                'Unsubscribe from the newsletter at any time via the link in any email.',
                'Object to or restrict certain processing of your data.',
              ]}
            />
            <p>
              To exercise any of these rights, please email us at{' '}
              <a
                href="mailto:support@codetobyte.com"
                style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
              >
                support@codetobyte.com
              </a>
              . We will respond within a reasonable timeframe.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Children's Privacy">
            <p style={{ marginTop: 0 }}>
              CodeToByte is not directed at children under the age of 13. We do not knowingly
              collect personal information from anyone under 13. If you believe a child has
              provided us with personal data, please contact us at{' '}
              <a
                href="mailto:support@codetobyte.com"
                style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
              >
                support@codetobyte.com
              </a>{' '}
              and we will delete it promptly.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Changes to This Policy">
            <p style={{ marginTop: 0 }}>
              We may update this Privacy Policy from time to time. When we do, we will revise
              the &ldquo;Last updated&rdquo; date at the top of this page. Continued use of
              CodeToByte after changes are posted constitutes your acceptance of the updated
              policy. We encourage you to review this page periodically.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Contact Us">
            <p style={{ marginTop: 0 }}>
              If you have any questions, concerns, or requests regarding this Privacy Policy or
              your personal data, please contact us:
            </p>
            <div
              style={{
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: '0.82rem',
                color: 'var(--text-secondary)',
                marginTop: '8px',
              }}
            >
              <div>
                <span style={{ color: 'var(--accent-green)' }}>email</span>{' '}
                →{' '}
                <a
                  href="mailto:support@codetobyte.com"
                  style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
                >
                  support@codetobyte.com
                </a>
              </div>
              <div style={{ marginTop: '4px' }}>
                <span style={{ color: 'var(--accent-green)' }}>website</span>{' '}
                → codetobyte.com
              </div>
            </div>
          </Section>

          {/* Back link */}
          <div style={{ marginTop: '8px' }}>
            <Link to="/" style={{ color: 'var(--accent-blue)', fontSize: '0.8rem', fontFamily: "'JetBrains Mono', 'Courier New', monospace" }}>
              [← back to home]
            </Link>
            {' '}
            <Link to="/terms" style={{ color: 'var(--accent-blue)', fontSize: '0.8rem', fontFamily: "'JetBrains Mono', 'Courier New', monospace", marginLeft: '12px' }}>
              [terms and conditions →]
            </Link>
          </div>
        </div>
      </TerminalWindow>
    </div>
  </>
)
